'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, Goal, H, Hl, Lesson, M, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/numbers'

const CoordinateGoal = dynamic(() => import('../_components/widgets/NumberWidgets').then(m => m.CoordinateGoal), { ssr: false })
const FunctionTracer = dynamic(() => import('../_components/widgets/NumberWidgets').then(m => m.FunctionTracer), { ssr: false })

export default function Graphs() {
  return (
    <Lesson>
      <Step gate="goal-point">
        <H>Two number lines make a map</H>
        <P>
          Take one number line going left–right and another going up–down, crossing at 0. Now every spot on the flat page has an
          address: <Hl c="blue">how far right</Hl> (called <M>x</M>) and <Hl c="amber">how far up</Hl> (called <M>y</M>). We
          write the address as <M>{'(x, y)'}</M> — always x first.
        </P>
        <P>
          This is the <strong>coordinate plane</strong>, invented by René Descartes so that shapes could be described with
          numbers. Every chart you’ll ever read is one.
        </P>
        <Goal id="goal-point" task={<>Drag the point to the address <M>{'(3, -2)'}</M>: three steps right, two steps down.</>}>
          {onReach => <CoordinateGoal target={[3, -2]} onGoal={onReach} />}
        </Goal>
      </Step>

      <Step>
        <H>A rule draws a picture</H>
        <P>
          Remember functions — machines that turn an input into an output? Feed a function every <Hl c="blue">x</Hl>, and plot
          each answer as a point <M>{'(x, f(x))'}</M>. With a few inputs you get a few dots. With more, the dots crowd together
          into a smooth curve: the <strong>graph</strong> of the function.
        </P>
        <Figure n="2.4" caption="Pick a rule. Slide ‘dots’ up to fill in the graph, and drag x to see one input-output pair at a time.">
          <FunctionTracer />
        </Figure>
        <P>
          The graph is the whole function at a glance: where it’s high, where it’s low, where it crosses zero. That’s why we’ll
          draw everything — including how wrong a model is — as a graph.
        </P>
      </Step>

      <Step gate="q-read">
        <H>Reading a graph</H>
        <Exercise id="plot-rule" title="Plot a rule" {...EX.plotRule} />
        <Quiz
          id="q-read"
          question={
            <>
              Looking at the graph of <M>{'f(x) = x^2 - 4'}</M>, what is its <em>lowest</em> output?
            </>
          }
          options={[
            { label: '0', why: 'The curve dips below the x-axis — so it goes lower than 0.' },
            { label: '−4', correct: true },
            { label: '−2', why: 'f(−2) = 0. That’s where it crosses zero, not its lowest point.' },
            { label: 'It keeps going down forever', why: 'It’s U-shaped: it comes down, turns around at the bottom, and goes back up.' },
          ]}
          explain="The bottom of the U is at x = 0, where f(0) = 0² − 4 = −4. Finding the lowest point of a curve is — spoiler — the entire game of machine learning."
        />
      </Step>

      <Step gate="zeros">
        <H>Finding where a function is zero</H>
        <P>
          The points where a graph crosses the x-axis are where <M>{'f(x) = 0'}</M>. You can find them by checking inputs one by
          one — the computer doesn’t mind trying hundreds.
        </P>
        <Exercise
          id="zeros"
          title="Search for zeros"
          {...EX.zeros}
          hints={[
            <>
              <C>{'for (let x = -10; x <= 10; x++)'}</C> visits every whole number from −10 to 10.
            </>,
            <>
              Inside: <C>{'if (f(x) === 0) found.push(x)'}</C>.
            </>,
          ]}
        />
        <Callout kind="why">
          Trying every input works for one number between −10 and 10. A real model has millions of numbers to choose — far too
          many combinations to try. Module 4 shows the clever alternative: follow the slope downhill.
        </Callout>
      </Step>
    </Lesson>
  )
}
